import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { cognitoConfig } from '../config/cognito';

// Initialize Cognito User Pool
const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId,
  ClientId: cognitoConfig.userPoolWebClientId,
});

// ============================================
// STORAGE KEYS
// ============================================
const STORAGE_KEYS = {
  ID_TOKEN: 'makemerry_id_token',
  ACCESS_TOKEN: 'makemerry_access_token',
  REFRESH_TOKEN: 'makemerry_refresh_token',
  USER_EMAIL: 'makemerry_user_email',
  TOKEN_EXPIRY: 'makemerry_token_expiry',
};

// ============================================
// TOKEN MANAGEMENT
// ============================================

/**
 * Save tokens to localStorage
 */
function saveTokens(session, email) {
  const idToken = session.getIdToken().getJwtToken();
  const accessToken = session.getAccessToken().getJwtToken();
  const refreshToken = session.getRefreshToken().getToken();
  const expiryTime = session.getIdToken().getExpiration() * 1000; // Convert to ms

  localStorage.setItem(STORAGE_KEYS.ID_TOKEN, idToken);
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email);
  localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());
}

/**
 * Get ID token from localStorage
 */
export function getIdToken() {
  return localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
}

/**
 * Get user email from localStorage
 */
export function getUserEmail() {
  return localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
}

/**
 * Check if token is expired
 */
function isTokenExpired() {
  const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
  if (!expiry) return true;

  const now = Date.now();
  const expiryTime = parseInt(expiry, 10);

  // Consider token expired 5 minutes before actual expiry (safety buffer)
  return now >= (expiryTime - 5 * 60 * 1000);
}

/**
 * Clear all auth data from localStorage
 */
function clearTokens() {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
}

// ============================================
// AUTHENTICATION METHODS
// ============================================

/**
 * Sign up a new user
 */
export function signUp(email, password, givenName, familyName) {
  return new Promise((resolve, reject) => {
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
    ];

    if (givenName) {
      attributeList.push(new CognitoUserAttribute({ Name: 'given_name', Value: givenName }));
    }
    if (familyName) {
      attributeList.push(new CognitoUserAttribute({ Name: 'family_name', Value: familyName }));
    }

    userPool.signUp(email, password, attributeList, null, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve({
        user: result.user,
        userConfirmed: result.userConfirmed,
        userSub: result.userSub,
      });
    });
  });
}

/**
 * Confirm email with verification code
 */
export function confirmSignUp(email, code) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
}

/**
 * Sign in user and store tokens
 */
export function signIn(email, password) {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        // Save tokens to localStorage
        saveTokens(session, email);

        resolve({
          idToken: session.getIdToken().getJwtToken(),
          email: email,
          userId: session.getIdToken().payload.sub,
        });
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Store cognitoUser for later use in completeNewPasswordChallenge
        resolve({
          challengeName: 'NEW_PASSWORD_REQUIRED',
          cognitoUser: cognitoUser,
          userAttributes: userAttributes,
          requiredAttributes: requiredAttributes,
        });
      },
    });
  });
}

/**
 * Complete new password challenge
 */
export function completeNewPasswordChallenge(cognitoUser, newPassword, userAttributes = {}) {
  return new Promise((resolve, reject) => {
    // Remove read-only attributes
    delete userAttributes.email_verified;
    delete userAttributes.phone_number_verified;

    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => {
        // Save tokens to localStorage
        const email = session.getIdToken().payload.email;
        saveTokens(session, email);

        resolve({
          idToken: session.getIdToken().getJwtToken(),
          email: email,
          userId: session.getIdToken().payload.sub,
        });
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

/**
 * Sign out user and clear tokens
 */
export function signOut() {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();

    if (cognitoUser) {
      cognitoUser.signOut();
    }

    clearTokens();
    resolve();
  });
}

/**
 * Get current session (with auto-refresh if needed)
 */
export function getCurrentSession() {
  return new Promise((resolve, reject) => {
    const email = getUserEmail();

    if (!email) {
      reject(new Error('No user session found'));
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.getSession((err, session) => {
      if (err) {
        clearTokens();
        reject(err);
        return;
      }

      if (!session.isValid()) {
        clearTokens();
        reject(new Error('Invalid session'));
        return;
      }

      // Update tokens in localStorage (in case they were refreshed)
      saveTokens(session, email);

      resolve({
        idToken: session.getIdToken().getJwtToken(),
        email: email,
        userId: session.getIdToken().payload.sub,
      });
    });
  });
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated() {
  const idToken = getIdToken();

  if (!idToken) {
    return false;
  }

  // If token is expired, try to refresh
  if (isTokenExpired()) {
    try {
      await getCurrentSession(); // This will auto-refresh
      return true;
    } catch (error) {
      clearTokens();
      return false;
    }
  }

  return true;
}

/**
 * Forgot password - request reset code
 */
export function forgotPassword(email) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: (result) => {
        resolve(result);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

/**
 * Confirm forgot password with code and new password
 */
export function confirmForgotPassword(email, code, newPassword) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

/**
 * Resend confirmation code
 */
export function resendConfirmationCode(email) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
}
